import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { format } from "date-fns"
import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  Info,
} from "lucide-react"
import { useState } from "react"
import type {
  ConformityV2DetailRow,
  ConformityV2Framework,
} from "../../../api/conformityApi"
import { dateFnsLocale } from "../../../utils/date"
import { formatPct } from "../../../utils/format"
import { McPill } from "../../shared/McPill"
import {
  frameworkRowPill,
  frameworkRowStatus,
  isBlockedStatus,
  registerLine,
  scipRowAnchorId,
} from "./frameworkPresentation"
import { GapList } from "./GapList"

/** One framework in the editor's evaluation pane (design canvas row):
 *  include checkbox · name + register line · status pill · SCIP action chip ·
 *  expandable detail (dependency note, override audit, component table with
 *  the inline own-SCIP add, exemption note, supplier-grouped gaps,
 *  unavailable note). */
/** Rows added per click of the fold. Paginated rather than "show all": a
 *  customer scope can carry thousands of rows, and mounting them all at once is
 *  the crawl the window exists to prevent. */
const ROW_WINDOW = 100

export function FrameworkResultRow({
  framework,
  expanded,
  readOnly,
  canOverride,
  canEditProducts,
  onToggleInclude,
  onToggleExpand,
  onHover,
  onJumpToBlock,
  onOverride,
  onClearOverride,
  onAddScip,
  overrideByName,
}: {
  framework: ConformityV2Framework
  expanded: boolean
  readOnly: boolean
  canOverride: boolean
  /** MC_MASTER_DATA_EDIT — the permission `useSetProductScipNumber` (the
   *  mutation behind the own-SCIP link) is guarded on. Without it the link
   *  would only fail on click, so the cell renders as a plain dash instead. */
  canEditProducts: boolean
  onToggleInclude: () => void
  onToggleExpand: () => void
  onHover: (key: string | null) => void
  onJumpToBlock?: () => void
  onOverride: () => void
  onClearOverride: () => void
  onAddScip: (productId: string, productNumber: string | null) => void
  overrideByName: string | null
}) {
  const { translate, currentLanguage } = useTranslation()
  const f = framework
  const status = frameworkRowStatus(f)
  const pill = frameworkRowPill(status, translate)
  const blocked = isBlockedStatus(status)
  const unavailable = status === "unavailable"
  const included = !blocked && !unavailable && f.selected
  const checkDisabled = blocked || unavailable || readOnly
  const scipPending =
    f.frameworkKey === "REACH_SVHC" && f.scipMissing.length > 0
  const showScip = f.frameworkKey === "REACH_SVHC"
  const fmtDate = (iso: string) =>
    format(new Date(iso), "dd MMM yyyy", {
      locale: dateFnsLocale(currentLanguage),
    })
  const svhcRows = f.rows.filter((r) => r.kind === "svhc")
  const exemptionRows = f.rows.filter((r) => r.kind === "exemption")
  const tableRows: ConformityV2DetailRow[] = showScip ? svhcRows : f.rows
  const [rowLimit, setRowLimit] = useState(ROW_WINDOW)
  // A customer-scope declaration can carry thousands of evidence rows, and every
  // one of them mounted at once is what makes the editor crawl. Rows whose
  // product still lacks its own SCIP number always stay in the window: the
  // refused panel scrolls to them by DOM id, and an anchor that isn't rendered
  // silently does nothing.
  // The window has to survive an SVHC-heavy scope: exempting EVERY row whose
  // product lacks a SCIP number put thousands back on the page, which is the
  // crawl this exists to stop. Only the FIRST row per missing product escapes —
  // that is all the refusal panel's scroll-to anchor needs.
  const anchoredProducts = new Set<string>()
  const visibleRows = tableRows.filter((row, i) => {
    if (i < rowLimit) {
      if (showScip && !row.ownScip) anchoredProducts.add(row.productId)
      return true
    }
    if (showScip && !row.ownScip && !anchoredProducts.has(row.productId)) {
      anchoredProducts.add(row.productId)
      return true
    }
    return false
  })
  const hiddenRowCount = tableRows.length - visibleRows.length
  const exemptionNote =
    exemptionRows.length > 0
      ? translate("MC_CONF_V2_EDIT_EXEMPTION_NOTE", {
          list: exemptionRows
            .map((e) =>
              e.expiryDate
                ? translate("MC_CONF_V2_EDIT_EXEMPTION_VALID_UNTIL", {
                    code: e.code ?? "",
                    date: fmtDate(e.expiryDate),
                  })
                : `${e.code ?? ""} — ${translate("MC_CONF_V2_EDIT_FLAG_EXEMPTION")}`,
            )
            .join(" · "),
        })
      : null

  return (
    <div
      data-testid={`fw-row-${f.frameworkKey}`}
      onMouseEnter={() => onHover(f.frameworkKey)}
      onMouseLeave={() => onHover(null)}
      className={cn(
        "rounded-xl border border-white/[0.07] bg-grey2 transition-colors hover:border-grey5",
        !included && !blocked && "opacity-60",
      )}
    >
      {/* The whole header is the expand/collapse control (and jumps to the
          document block); only the include checkbox is its own control. */}
      <div
        role="button"
        tabIndex={0}
        aria-label={translate("MC_CONF_V2_EDIT_EXPAND")}
        aria-expanded={expanded}
        onClick={() => {
          onJumpToBlock?.()
          onToggleExpand()
        }}
        onKeyDown={(event) => {
          // Only keys pressed ON the header: Enter/Space on the include
          // checkbox bubble up here and must keep activating the checkbox.
          if (event.target !== event.currentTarget) return
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onToggleExpand()
          }
        }}
        className="grid cursor-pointer select-none grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-3.5 py-3 outline-hidden focus-visible:bg-white/3"
      >
        <button
          type="button"
          role="checkbox"
          aria-checked={included}
          aria-label={f.label}
          disabled={checkDisabled}
          onClick={(event) => {
            event.stopPropagation()
            onToggleInclude()
          }}
          className={cn(
            "flex h-4 w-4 items-center justify-center rounded border-[1.5px] p-0",
            included
              ? "border-yellow1 bg-yellow1"
              : "border-grey4 bg-transparent",
            checkDisabled && "opacity-35",
          )}
        >
          {included && (
            <Check size={10} strokeWidth={3} className="text-black" />
          )}
        </button>
        <div className="min-w-0 text-left text-white">
          <div className="text-[13.5px] font-semibold leading-tight">
            {f.label}
          </div>
          <div className="mt-0.5 truncate text-[11.5px] text-grey5">
            {registerLine(f, translate, fmtDate)}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <McPill tone={pill.tone}>{pill.label}</McPill>
          {scipPending && (
            <McPill tone="attention" icon={AlertCircle}>
              {translate("MC_CONF_V2_EDIT_SCIP_PENDING", {
                n: f.scipMissing.length,
              })}
            </McPill>
          )}
        </div>
        <span
          aria-hidden="true"
          className={cn(
            "p-1 text-grey5 transition-transform",
            expanded && "rotate-180",
          )}
        >
          <ChevronDown size={16} />
        </span>
      </div>

      {expanded && (
        <div className="flex flex-col gap-2.5 border-t border-white/[0.07] px-3.5 py-3">
          {f.frameworkKey === "PFAS" && (
            <div className="flex items-center gap-1.5 text-[11.5px] text-grey5">
              <Info size={12} />
              {/* The standing note explains the coupling ("deselect XVII and PFAS
                  goes too"), which is the wrong advice when XVII was never adopted
                  — there is nothing to deselect. Name the actual blocker instead. */}
              {translate(
                f.blockedReason === "dependency_missing"
                  ? "MC_CONF_V2_BLOCKED_DEPENDENCY_MISSING"
                  : "MC_CONF_V2_EDIT_DEPENDENCY_NOTE",
              )}
            </div>
          )}

          {f.overrideReason && (
            <div className="border-l-2 border-grey4 pl-2.5 text-[12px] leading-relaxed text-grey5">
              {translate("MC_CONF_V2_EDIT_OVERRIDDEN_NOTE", {
                who: overrideByName ?? f.overrideBy ?? "—",
                date: f.overrideAt ? fmtDate(f.overrideAt) : "—",
              })}
              “{f.overrideReason}”
              {canOverride && !readOnly && (
                <button
                  type="button"
                  onClick={onClearOverride}
                  className="ml-2 border-0 bg-transparent p-0 text-[11px] text-grey5 underline underline-offset-2 hover:text-white"
                >
                  {translate("MC_CONF_V2_OVERRIDE_CLEAR")}
                </button>
              )}
            </div>
          )}

          {!blocked && !unavailable && tableRows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-white/[0.07]">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-grey1">
                    <Th>{translate("MC_CONF_V2_EDIT_TH_PRODUCT")}</Th>
                    <Th>{translate("MC_CONF_V2_EDIT_TH_COMPONENT")}</Th>
                    <Th>{translate("MC_CONF_V2_EDIT_TH_SUBSTANCE")}</Th>
                    <Th right>{translate("MC_CONF_V2_EDIT_TH_MAX")}</Th>
                    {showScip && (
                      <>
                        <Th>{translate("MC_CONF_V2_EDIT_TH_SUP_SCIP")}</Th>
                        <Th>{translate("MC_CONF_V2_EDIT_TH_OWN_SCIP")}</Th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, i) => {
                    const ownScipMissing = showScip && !row.ownScip
                    return (
                      <tr
                        key={`${row.productId}-${row.articleId}-${i}`}
                        id={
                          ownScipMissing
                            ? scipRowAnchorId(row.productId)
                            : undefined
                        }
                        tabIndex={ownScipMissing ? -1 : undefined}
                        className={cn(
                          "border-t border-white/[0.07] focus:outline-hidden",
                          ownScipMissing &&
                            "[&>td:first-child]:shadow-[inset_2px_0_0_#FFF440] [&>td]:bg-yellow1/6 [&>td]:focus:bg-yellow1/[0.14]",
                        )}
                      >
                        <Td>
                          <span className="font-mono text-white">
                            {row.productNumber}
                          </span>
                          <div className="text-[11px] text-grey5">
                            {row.productName}
                          </div>
                        </Td>
                        <Td muted>
                          {row.articleName ?? row.articleNumber}
                          <div className="text-[11px] text-grey5">
                            {row.supplierName}
                          </div>
                        </Td>
                        <Td>
                          {row.substance ? (
                            <>
                              <span className="text-white">
                                {row.substance}
                              </span>
                              <div className="font-mono text-[11px] text-grey5">
                                {row.cas}
                              </div>
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-warning">
                              <AlertTriangle size={11} />
                              {translate("MC_CONF_V2_EDIT_FLAG_SUBSTANCE")}
                            </span>
                          )}
                          {row.kind === "exemption" && !row.expiryDate && (
                            <div className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] text-warning">
                              <AlertTriangle size={11} />
                              {translate("MC_CONF_V2_EDIT_FLAG_EXEMPTION")}
                            </div>
                          )}
                        </Td>
                        <Td right>
                          <span className="font-mono whitespace-nowrap text-white">
                            {row.maxConcentrationPct != null
                              ? `≤ ${formatPct(row.maxConcentrationPct, currentLanguage)} %`
                              : "—"}
                          </span>
                        </Td>
                        {showScip && (
                          <>
                            <Td>
                              <span className="font-mono break-all text-[11.5px] text-grey5">
                                {row.supplierScip ?? "—"}
                              </span>
                            </Td>
                            <Td>
                              {row.ownScip ? (
                                <span className="font-mono break-all text-[11.5px] text-grey5">
                                  {row.ownScip}
                                </span>
                              ) : readOnly ? (
                                <span className="text-[11.5px] text-grey5">
                                  —
                                </span>
                              ) : !canEditProducts ? (
                                <span
                                  className="text-[11.5px] text-grey5"
                                  title={translate(
                                    "MC_CONF_V2_EDIT_SCIP_NO_PERMISSION",
                                  )}
                                >
                                  —
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    onAddScip(row.productId, row.productNumber)
                                  }
                                  className="border-0 bg-transparent p-0 text-[11.5px] text-warning underline underline-offset-2"
                                >
                                  {translate("MC_CONF_V2_EDIT_ADD_ON_PRODUCT")}
                                </button>
                              )}
                            </Td>
                          </>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {hiddenRowCount > 0 && (
                <button
                  type="button"
                  onClick={() => setRowLimit((n) => n + ROW_WINDOW)}
                  className="w-full border-0 border-t border-white/[0.07] bg-grey1 px-3 py-2 text-[11.5px] text-grey5 underline underline-offset-2 hover:text-white"
                >
                  {translate("MC_CONF_V2_EDIT_SHOW_ALL_ROWS", {
                    count: String(Math.min(ROW_WINDOW, hiddenRowCount)),
                  })}
                </button>
              )}
            </div>
          )}

          {exemptionNote && (
            <div className="text-[11.5px] text-grey5">{exemptionNote}</div>
          )}

          {blocked && (
            <GapList
              gaps={f.gaps}
              onOverride={
                canOverride && f.blockedReason === "gap" && !readOnly
                  ? onOverride
                  : undefined
              }
              overrideHidden={readOnly || f.blockedReason !== "gap"}
              overridable={f.overridable}
              overrideBlocker={f.overrideBlocker}
            />
          )}

          {unavailable && (
            // Registers are Atlas-managed — there is no org-facing screen to
            // link, so this stays a hint (review NF7).
            <div className="text-[12px] text-grey5">
              {translate("MC_CONF_V2_EDIT_UNAVAILABLE_BODY", {
                name: f.label,
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Th({
  children,
  right,
}: {
  children: React.ReactNode
  right?: boolean
}) {
  return (
    <th
      className={cn(
        "font-mono px-2.5 py-2 text-[10.5px] font-medium uppercase tracking-[0.06em] text-grey5",
        right ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  right,
  muted,
}: {
  children: React.ReactNode
  right?: boolean
  muted?: boolean
}) {
  return (
    <td
      className={cn(
        "px-2.5 py-2 align-top text-[12px]",
        right && "text-right",
        muted && "text-grey5",
      )}
    >
      {children}
    </td>
  )
}
